const TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const EXPIRES_AT_KEY = 'tokenExpiresAt';
const API_TIMEOUT_MS = 20000;

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getTokenExpiresAt(): number | null {
  const raw = localStorage.getItem(EXPIRES_AT_KEY);
  return raw ? parseInt(raw, 10) : null;
}

export function setTokens(
  accessToken: string,
  refreshToken: string,
  expiresAt?: number
): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  if (expiresAt !== undefined) {
    localStorage.setItem(EXPIRES_AT_KEY, String(expiresAt));
  }
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(EXPIRES_AT_KEY);
}

async function refreshAccessToken(): Promise<
  string | null
> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    const json = await res.json();

    if (!res.ok || (json && json.success === false)) {
      clearTokens();
      return null;
    }

    // Unwrap from envelope if present
    const data = json.data ?? json;
    setTokens(
      data.accessToken,
      data.refreshToken,
      data.expiresAt
    );
    return data.accessToken;
  } catch {
    clearTokens();
    return null;
  }
}

export interface ApiError {
  error: string;
  status: number;
  traceId?: string;
  code?: string;
}

function extractErrorMessage(
  raw: string | null,
  json: any
): string {
  if (json?.error) return json.error;
  // Raw Netlify Lambda error format: {"errorType":"Error","errorMessage":"...","trace":[...]}
  if (json?.errorMessage) return json.errorMessage;
  return raw || 'Unknown error';
}

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: ApiError };

export async function api<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResult<T>> {
  const token = getAccessToken();
  const method = (options.method || 'GET').toUpperCase();

  const makeRequest = async (
    authToken: string | null
  ): Promise<Response> => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (authToken) {
      (headers as Record<string, string>)['Authorization'] =
        `Bearer ${authToken}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      API_TIMEOUT_MS
    );

    try {
      const response = await fetch(path, {
        ...options,
        headers,
        signal: controller.signal,
      });
      return response;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const makeRequestWithRetry = async (
    authToken: string | null
  ): Promise<Response> => {
    const maxAttempts = method === 'GET' ? 3 : 1;
    let lastErr: unknown;

    for (
      let attempt = 1;
      attempt <= maxAttempts;
      attempt++
    ) {
      try {
        const response = await makeRequest(authToken);

        // Netlify can sometimes return a truncated/invalid lambda response.
        // This tends to show up as a 502 with a body containing this string.
        // Only retry idempotent GET requests.
        if (method === 'GET' && response.status === 502) {
          try {
            const body = await response.clone().text();
            if (
              body
                .toLowerCase()
                .includes('error decoding lambda response')
            ) {
              if (attempt < maxAttempts) {
                await new Promise(r =>
                  setTimeout(r, 300 * attempt)
                );
                continue;
              }
            }
          } catch {
            // If we can't read the body, don't assume it's retryable.
          }
        }

        return response;
      } catch (err) {
        lastErr = err;
        if (
          err instanceof Error &&
          err.name === 'AbortError'
        ) {
          return new Response('Request timeout', {
            status: 408,
          });
        }
        if (method === 'GET' && attempt < maxAttempts) {
          await new Promise(r =>
            setTimeout(r, 300 * attempt)
          );
          continue;
        }
        throw err;
      }
    }

    throw lastErr;
  };

  let res: Response;
  try {
    res = await makeRequestWithRetry(token);
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      return {
        ok: false,
        error: { error: 'Request timeout', status: 408 },
      };
    }
    throw err;
  }

  // If unauthorized, try refreshing the token once
  if (res.status === 401 && token) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      try {
        res = await makeRequestWithRetry(newToken);
      } catch (err) {
        if (
          err instanceof Error &&
          err.name === 'AbortError'
        ) {
          return {
            ok: false,
            error: {
              error: 'Request timeout',
              status: 408,
            },
          };
        }
        throw err;
      }
    } else {
      // Token refresh failed - trigger global logout event
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
  }

  // Extract Netlify trace ID from response headers
  const traceId =
    res.headers.get('x-nf-trace-id') ||
    res.headers.get('x-nf-request-id') ||
    undefined;

  let json: any = null;
  let text: string | null = null;
  try {
    text = await res.text();
  } catch {
    text = null;
  }

  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }

  // Check HTTP-level errors first (401, 400, 500)
  if (!res.ok) {
    const error: ApiError = {
      error: extractErrorMessage(text, json),
      status: res.status,
      traceId,
      ...(res.status === 401
        ? { code: 'UNAUTHORIZED' }
        : {}),
    };

    // Log errors more prominently
    if (res.status === 404) {
      console.error(
        `❌ [API 404] Route not found: ${method} ${path}`,
        {
          error,
          traceId,
          response: json || text,
        }
      );
    } else if (traceId) {
      console.error(
        `[API Error] Netlify Trace ID: ${traceId}`,
        error
      );
    } else {
      console.error(
        `❌ [API ${res.status}] ${method} ${path}`,
        error
      );
    }

    return {
      ok: false,
      error,
    };
  }

  // Check application-level errors (business logic failures return 200 with success: false)
  if (json && json.success === false) {
    const error: ApiError = {
      error: extractErrorMessage(text, json),
      status: res.status,
      traceId,
    };

    // Log trace ID to console for debugging
    if (traceId) {
      console.error(
        `[API Error] Netlify Trace ID: ${traceId}`,
        error
      );
    }

    return {
      ok: false,
      error,
    };
  }

  // Success: unwrap data from {success: true, data: ...} envelope
  if (json == null || typeof json !== 'object') {
    return {
      ok: false,
      error: {
        error: text || 'Invalid response',
        status: res.status,
        traceId,
      },
    };
  }

  return { ok: true, data: (json.data ?? json) as T };
}

// Convenience methods
export const apiGet = <T>(path: string) =>
  api<T>(path, { method: 'GET' });

export const apiPost = <T>(path: string, body?: unknown) =>
  api<T>(path, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });

export const apiPut = <T>(path: string, body?: unknown) =>
  api<T>(path, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });

export const apiDelete = <T>(path: string) =>
  api<T>(path, { method: 'DELETE' });

export const apiPatch = <T>(path: string, body?: unknown) =>
  api<T>(path, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
